import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Instagram, Facebook } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { waLink, CONTACT, SOCIAL, API } from "../config";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      setDone(true);
      toast.success("You're on the list — festive offers coming your way.");
    } catch {
      toast.error("Couldn't subscribe. Please try again.");
    }
  };

  if (done) {
    return <p data-testid="newsletter-success" className="mt-6 text-xs tracking-[0.2em] uppercase text-gold">You're on the list. Thank you!</p>;
  }

  return (
    <form onSubmit={submit} className="mt-6 max-w-xs">
      <p className="text-[10px] tracking-[0.3em] uppercase text-cream/40 mb-3">Festive offers & oil wisdom</p>
      <div className="flex gap-2">
        <input
          data-testid="newsletter-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="flex-1 min-w-0 rounded-full border border-cream/20 bg-transparent px-4 py-2.5 text-sm text-cream placeholder:text-cream/30 focus:border-gold focus:outline-none"
        />
        <button
          data-testid="newsletter-subscribe-btn"
          type="submit"
          className="rounded-full bg-gold px-5 py-2.5 text-xs tracking-widest uppercase font-medium text-ink hover:bg-cream transition-colors duration-300"
        >
          Join
        </button>
      </div>
    </form>
  );
};

const Column = ({ title, links }) => (
  <div>
    <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-5">{title}</p>
    <div className="flex flex-col gap-3 text-sm font-light text-cream/60">
      {links.map((l) =>
        l.external ? (
          <a
            key={l.testid}
            data-testid={l.testid}
            href={l.to}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gold transition-colors duration-300 w-fit"
          >
            {l.label}
          </a>
        ) : (
          <Link
            key={l.testid}
            data-testid={l.testid}
            to={l.to}
            className="hover:text-gold transition-colors duration-300 w-fit"
          >
            {l.label}
          </Link>
        )
      )}
    </div>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="relative bg-ink grain overflow-hidden">
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-10 border-b border-white/10 pb-14">
        <div className="col-span-2">
          <img
            src="/images/phudg-logo-light.png"
            alt="PhudG"
            data-testid="footer-logo"
            className="h-11 w-auto mb-4"
          />
          <div className="flex items-center gap-3 mb-6">
            <img src="/images/asafu-logo.png" alt="ASAFU" className="h-9 w-auto rounded-lg" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-cream/40">
              An {CONTACT.company} brand
            </p>
          </div>
          <p className="max-w-xs text-sm font-light text-cream/50 leading-relaxed">
            Cold-pressed oils made the slow way — wooden ghani, small batches,
            below 45°C. Here's our seed, how we press it, and what's in the
            bottle.
          </p>
          <NewsletterForm />
        </div>
        <Column
          title="Shop"
          links={[
            { label: "Black Mustard Oil", to: "/oils/black-mustard-oil", testid: "footer-shop-black-mustard" },
            { label: "Yellow Mustard Oil", to: "/oils/yellow-mustard-oil", testid: "footer-shop-yellow-mustard" },
            { label: "Groundnut Oil", to: "/oils/groundnut-oil", testid: "footer-shop-groundnut" },
            { label: "Sunflower Oil", to: "/oils/sunflower-oil", testid: "footer-shop-sunflower" },
          ]}
        />
        <Column
          title="Learn"
          links={[
            { label: "Our Process", to: "/process", testid: "footer-learn-process" },
            { label: "Journal", to: "/journal", testid: "footer-learn-journal" },
            { label: "FAQs", to: "/faq", testid: "footer-learn-faq" },
            { label: "Where to Buy", to: "/where-to-buy", testid: "footer-learn-where-to-buy" },
          ]}
        />
        <Column
          title="About"
          links={[
            { label: "Our Story", to: "/about", testid: "footer-about-story" },
            { label: "Contact", to: "/contact", testid: "footer-about-contact" },
          ]}
        />
        <Column
          title="Customer Care"
          links={[
            { label: "Shipping", to: "/legal/shipping", testid: "footer-care-shipping" },
            { label: "Returns & Refunds", to: "/legal/refunds", testid: "footer-care-refunds" },
            { label: "Privacy Policy", to: "/legal/privacy", testid: "footer-care-privacy" },
            { label: "Terms & Conditions", to: "/legal/terms", testid: "footer-care-terms" },
          ]}
        />
      </div>

      <div className="flex items-center gap-4 py-8 border-b border-white/10">
        <span className="text-[10px] tracking-[0.35em] uppercase text-cream/40">Connect</span>
        <a data-testid="footer-instagram-link" href={SOCIAL.instagram} aria-label="Instagram" className="text-cream/50 hover:text-gold transition-colors duration-300">
          <Instagram size={18} />
        </a>
        <a data-testid="footer-facebook-link" href={SOCIAL.facebook} aria-label="Facebook" className="text-cream/50 hover:text-gold transition-colors duration-300">
          <Facebook size={18} />
        </a>
        <a
          data-testid="footer-whatsapp-link"
          href={waLink("Hi PHUDG! I'd like to order cold-pressed oils.")}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="text-cream/50 hover:text-gold transition-colors duration-300"
        >
          <MessageCircle size={18} />
        </a>
      </div>

      <p
        data-testid="footer-giant-logo"
        className="text-outline-gold font-serif font-semibold text-center leading-none tracking-tighter select-none text-[26vw] md:text-[20vw] -my-4 md:-my-10"
      >
        PHUDG
      </p>

      <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-8 text-xs font-light text-cream/40">
        <p data-testid="footer-copyright">
          © {new Date().getFullYear()} {CONTACT.company} · FSSAI Lic. No. {CONTACT.fssai}
        </p>
        <div className="flex items-center gap-5">
          <a
            data-testid="footer-email-link"
            href={`mailto:${CONTACT.email}`}
            className="hover:text-gold transition-colors duration-300"
          >
            {CONTACT.email}
          </a>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="hover:text-gold transition-colors duration-300"
          >
            Admin
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
